/**
 * Step Toolbox Component
 *
 * Sidebar containing draggable step types for the workflow builder.
 */
import { useState } from 'react';
import { useDraggable } from '@dnd-kit/core';
import { CSS } from '@dnd-kit/utilities';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Agent } from '../../types/agent';
import { useAgents } from '../../hooks/useAgents';
import { LoadingSkeleton } from '../common/LoadingSkeleton';

/**
 * Step type definition for the toolbox
 */
export interface StepType {
  id: string;
  type: 'agent' | 'condition' | 'delay' | 'transform';
  label: string;
  icon: string;
  description: string;
  agentId?: string;
  agentName?: string;
}

/**
 * Built-in step types
 */
const BUILT_IN_STEPS: StepType[] = [
  {
    id: 'condition',
    type: 'condition',
    label: 'Condition',
    icon: '🔀',
    description: 'Branch based on conditions',
  },
  {
    id: 'delay',
    type: 'delay',
    label: 'Delay',
    icon: '⏱️',
    description: 'Wait for a specified time',
  },
  {
    id: 'transform',
    type: 'transform',
    label: 'Transform',
    icon: '🔄',
    description: 'Transform data between steps',
  },
];

/**
 * Draggable step item in the toolbox
 */
interface DraggableStepProps {
  step: StepType;
}

function DraggableStep({ step }: DraggableStepProps) {
  const { attributes, listeners, setNodeRef, transform, isDragging } = useDraggable({
    id: `toolbox-${step.id}`,
    data: {
      type: 'toolbox-item',
      step,
    },
  });

  const style = {
    transform: CSS.Translate.toString(transform),
    opacity: isDragging ? 0.5 : 1,
  };

  return (
    <div
      ref={setNodeRef}
      style={style}
      {...listeners}
      {...attributes}
      className="p-3 bg-white border border-slate-200 rounded-lg cursor-grab hover:border-primary hover:shadow-sm transition-all active:cursor-grabbing"
    >
      <div className="flex items-center gap-3">
        <span className="text-2xl">{step.icon}</span>
        <div className="flex-1 min-w-0">
          <p className="font-medium text-sm truncate">{step.label}</p>
          <p className="text-xs text-muted-foreground truncate">{step.description}</p>
        </div>
      </div>
    </div>
  );
}

/**
 * Step Toolbox Component
 */
export function StepToolbox() {
  const { data, isLoading } = useAgents({ limit: 100, status: 'ACTIVE' });
  const agents = data?.data || [];
  const [query, setQuery] = useState('');

  // Convert agents to step types
  const agentSteps: StepType[] = agents.map((agent: Agent) => ({
    id: `agent-${agent.id}`,
    type: 'agent' as const,
    label: agent.name,
    icon: getAgentIcon(agent.type),
    description: agent.description || `${agent.type} agent`,
    agentId: agent.id,
    agentName: agent.name,
  }));

  const q = query.trim().toLowerCase();
  const filteredAgents = q
    ? agentSteps.filter(
        (s) =>
          s.label.toLowerCase().includes(q) ||
          s.description.toLowerCase().includes(q)
      )
    : agentSteps;

  return (
    <div className="w-64 bg-slate-50 border-r border-slate-200 flex flex-col">
      {/* Header (fixed) */}
      <div className="p-4 pb-2 shrink-0">
        <h2 className="text-lg font-semibold">Step Toolbox</h2>
        <p className="text-xs text-muted-foreground mt-1">
          Drag steps onto the canvas to build your workflow
        </p>
      </div>

      {/* Flow Control — short + always visible, no scrolling to reach it */}
      <div className="px-4 shrink-0">
        <Card>
          <CardHeader className="py-2 px-4">
            <CardTitle className="text-sm flex items-center gap-2">
              <span>🛠️</span>
              Flow Control
            </CardTitle>
          </CardHeader>
          <CardContent className="px-4 pb-3 pt-0 space-y-2">
            {BUILT_IN_STEPS.map((step) => (
              <DraggableStep key={step.id} step={step} />
            ))}
          </CardContent>
        </Card>
      </div>

      {/* Agents — searchable, scrolls within its own region */}
      <div className="flex flex-col flex-1 min-h-0 px-4 pb-4 pt-3">
        <div className="flex items-center justify-between mb-2 shrink-0">
          <div className="text-sm font-semibold flex items-center gap-2">
            <span>🤖</span> Agents
          </div>
          {agentSteps.length > 0 && (
            <span className="text-xs text-muted-foreground">
              {q ? `${filteredAgents.length}/${agentSteps.length}` : agentSteps.length}
            </span>
          )}
        </div>
        <input
          type="text"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder="Search agents…"
          className="h-9 w-full rounded-md border border-input bg-white px-3 text-sm mb-2 shrink-0 focus:outline-none focus:ring-2 focus:ring-ring"
        />
        <div className="flex-1 min-h-0 overflow-y-auto space-y-2 pr-1">
          {isLoading ? (
            <>
              <LoadingSkeleton className="h-16 w-full" />
              <LoadingSkeleton className="h-16 w-full" />
              <LoadingSkeleton className="h-16 w-full" />
            </>
          ) : agentSteps.length === 0 ? (
            <p className="text-xs text-muted-foreground py-2">
              No active agents available. Create agents first.
            </p>
          ) : filteredAgents.length === 0 ? (
            <p className="text-xs text-muted-foreground py-2">
              No agents match “{query}”.
            </p>
          ) : (
            filteredAgents.map((step) => (
              <DraggableStep key={step.id} step={step} />
            ))
          )}
        </div>
      </div>
    </div>
  );
}

/**
 * Get icon for agent type
 */
function getAgentIcon(type: string): string {
  switch (type) {
    case 'CUSTOMER_SUPPORT':
      return '💬';
    case 'DATA_PROCESSING':
      return '📊';
    case 'WORKFLOW_AUTOMATION':
      return '⚙️';
    case 'CONTENT_GENERATION':
      return '✍️';
    case 'ANALYSIS':
      return '🔍';
    case 'INTEGRATION':
      return '🔗';
    case 'CUSTOM':
      return '🎯';
    default:
      return '🤖';
  }
}

export default StepToolbox;
